/**
 * Last line of defense: prove that the bytes about to be written are redacted.
 *
 * Since redaction stopped being burnt into stored pixels, every safeguard sits
 * *upstream* of the writer — the store lookup, the loader hook, the cache
 * eviction. All of them fail silently by construction: `applyTo` returning early
 * because a key didn't match looks exactly like the (normal) case of an instance
 * with nothing to redact, so a miss produces a clean, successful export of
 * unredacted PHI.
 *
 * This check closes that off at the one place every exported pixel passes through
 * — the writers' per-frame buffer — by re-reading the same rectangles and
 * confirming the region really is filled. It catches a key mismatch, a loader that
 * stopped being registered, a reordering that moves `applyTo` before the imageId
 * is corrected, a cached frame that predates the commit, or a future decode path
 * that bypasses the loaders entirely. Any of those becomes a thrown error that
 * aborts the export instead of a silent leak.
 *
 * Cost is O(redacted area) per frame over a buffer that is already hot, which is
 * nothing next to gzip. It runs on the *output* bytes (post alpha-strip), so it
 * also covers the RGBA -> RGB conversion.
 */

import { TypedArray } from "../types/ImageFrame";
import * as redactionStore from "../redaction/redaction-store";

export class RedactionVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RedactionVerificationError";
  }
}

/**
 * Throw unless every sample inside this instance's committed rectangles equals
 * `fillValue` in `out`.
 *
 * `out` must be exactly what will be written for this frame, and `fillValue` the
 * value the applier used for the same decoded frame
 * (`redactionStore.fillValueFor(image)`).
 *
 * No-op for instances with no committed rectangles.
 */
export function assertFrameRedacted(
  instanceImageId: string,
  frameImageId: string,
  out: TypedArray,
  rows: number,
  columns: number,
  fillValue: number
): void {
  const rects = redactionStore.get(instanceImageId);
  if (!rects?.length) return;

  if (!rows || !columns) {
    throw new RedactionVerificationError(
      `Cannot verify redaction for ${frameImageId}: frame has no dimensions ` +
        `(rows=${rows}, columns=${columns}).`
    );
  }

  const samplesPerPixel = redactionStore.samplesPerPixelOf(out.length, rows, columns);

  for (const [rectIndex, rect] of rects.entries()) {
    const { startOffsetOfRow, rowSpan, top, height } = redactionStore.clampRect(
      rect,
      rows,
      columns,
      samplesPerPixel
    );
    for (let i = 0; i < height; i++) {
      const startIndex = startOffsetOfRow(i);
      for (let j = startIndex; j < startIndex + rowSpan; j++) {
        if (out[j] !== fillValue) {
          const column = Math.floor((j - startIndex) / samplesPerPixel) + rect.left;
          throw new RedactionVerificationError(
            `Redaction verification FAILED for ${frameImageId}: rectangle ${rectIndex + 1} ` +
              `of ${rects.length} (${rect.width}x${rect.height} at ${rect.left},${rect.top}) ` +
              `is not redacted — pixel at row ${top + i}, column ${column} is ${out[j]}, ` +
              `expected ${fillValue}. Export aborted; no unredacted pixel data was written.`
          );
        }
      }
    }
  }
}
